import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { from } from 'rxjs';
import { concatMap, finalize } from 'rxjs/operators';
import { DualLangInputComponent } from '../../../../../../form-builder/lib/dual-lang-input/dual-lang-input.component';
import { FormService } from '../../../../../../form-builder/services/form/form.service';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import { CustomWorkflowService } from '../custom-workflow.service';
import {
  CustomWorkflow,
  CustomWorkflowApiError,
  CustomWorkflowField,
  CustomWorkflowFieldOptions,
  CustomWorkflowSection,
  Localized,
  localized,
  localizedGroup,
  localizedOrNull,
} from '../custom-workflow.model';
import { CustomWorkflowFieldEditorComponent } from '../custom-workflow-field-editor/custom-workflow-field-editor.component';
import { FormBuilderFieldStaticPreviewComponent } from '../../../../../../form-builder/lib/form-builder-field/form-builder-field-preview/form-builder-field-static-preview.component';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { ActionListComponent } from '../../../../../common/components/action-list/action-list.component';
import { ActionList } from '../../../../../common/models/action-list.model';
import { inputTypesEnum } from 'all-libraries/ibusiness-core/src/form-builder/enums/input-type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-custom-workflow-form-editor',
  imports: [
    TranslocoPipe,
    ReactiveFormsModule,
    DualLangInputComponent,
    ValidationMessageComponent,
    CdkDropList,
    CdkDrag,
    CustomWorkflowFieldEditorComponent,
    FormBuilderFieldStaticPreviewComponent,
    BadgeComponent,
    ActionListComponent,
  ],
  templateUrl: './custom-workflow-form-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomWorkflowFormEditorComponent implements OnInit {
  readonly inputTypesEnum = inputTypesEnum;

  readonly service = inject(CustomWorkflowService);
  private readonly transloco = inject(TranslocoService);
  private readonly formService = inject(FormService);
  private readonly modalService = inject(ModalServiceService);
  private readonly lang = this.transloco.getActiveLang();
  private readonly destroyRef = inject(DestroyRef);

  workflow = input.required<CustomWorkflow>();
  readonly = input<boolean>(false);
  changed = output<void>();
  apiError = output<CustomWorkflowApiError>();

  readonly discardTemplate = viewChild<TemplateRef<unknown>>('discard');
  readonly fieldEditor = viewChild(CustomWorkflowFieldEditorComponent);
  fieldOptions = signal<CustomWorkflowFieldOptions>({
    types: [],
    lookup_models: [],
    lookup_variables: [],
    attachment_types: [],
  });
  editing = signal<{
    sectionId: number;
    field: CustomWorkflowField | null;
  } | null>(null);
  renamingId = signal<number | null>(null);
  reorderingFieldId = signal<number | null>(null);
  renameForm = new FormGroup({ label: localizedGroup() });
  dirty = computed(
    () => (this.fieldEditor()?.dirty() ?? false) || this.renamingId() !== null
  );
  editorTitle = computed(() =>
    this.transloco.translate(
      this.editing()?.field
        ? 'custom_workflow_edit_field_title'
        : 'custom_workflow_add_field_button'
    )
  );
  private fieldActionsFor(field: CustomWorkflowField): ActionList[] {
    const actions: ActionList[] = [
      { key: 'edit', label: 'Edit', hasIcon: true },
    ];
    if (!this.workflow().locked && !this.readonly()) {
      actions.push({ key: 'duplicate', label: 'Duplicate', hasIcon: true });
      actions.push({ key: 'delete', label: 'Delete', hasIcon: true });
    }
    return actions;
  }

  /** Sections and fields with their display labels resolved, so the template calls nothing. */
  sectionViews = computed(() => {
    const sections = this.workflow().sections;
    return sections.map((section, i) => ({
      section,
      title: section.label,
      actions: this.sectionActions(section),
      canMoveUp: i > 0,
      canMoveDown: i < sections.length - 1,
      fields: section.fields.map(field => ({
        field,
        title: field.label,
        typeLabel: this.typeLabel(field.type),
        actions: this.fieldActionsFor(field),
        placeholder: localized(field.metadata.placeholder, this.lang),
        hint: localized(field.metadata.hint, this.lang),
      })),
    }));
  });

  private sectionActions(section: CustomWorkflowSection): ActionList[] {
    if (this.workflow().locked || this.readonly()) return [];
    const actions: ActionList[] = [
      { key: 'duplicate', label: 'Duplicate', hasIcon: true },
    ];
    if (this.workflow().sections.length > 1) {
      actions.push({ key: 'delete', label: 'Delete', hasIcon: true });
    }
    return actions;
  }

  ngOnInit() {
    this.service
      .fieldOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        // TODO: remove once filters are implemented for the bank_account lookup model.
        this.fieldOptions.set({
          ...res.data,
          lookup_models: res.data.lookup_models.filter(
            model => model.key !== 'bank_account'
          ),
        });
      });
  }

  private typeLabel(type: string): string {
    const key = `custom_workflow_field_type_${type}`;
    const translated = this.transloco.translate(key);
    return translated === key
      ? (this.fieldOptions().types.find(t => t.value === type)?.label ?? type)
      : translated;
  }

  startNewField(sectionId: number) {
    this.editing.set({ sectionId, field: null });
  }

  editField(field: CustomWorkflowField) {
    this.editing.set({ sectionId: field.section_id, field });
  }

  requestClose() {
    if (this.fieldEditor()?.dirty()) {
      this.modalService.modalContent.set({
        isOpen: true,
        hasCloseIcon: false,
        title: '',
        customizeContent: this.discardTemplate(),
      });
      return;
    }
    this.editing.set(null);
  }

  discardAndClose() {
    this.modalService.modalContent.set({ isOpen: false });
    this.editing.set(null);
  }

  keepEditing() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  onFieldAction(key: string, field: CustomWorkflowField) {
    if (key === 'edit') this.editField(field);
    else if (key === 'duplicate') this.duplicateField(field);
    else if (key === 'delete') this.deleteField(field);
  }

  duplicateField(field: CustomWorkflowField) {
    this.service
      .createField(
        this.workflow().id,
        this.duplicateFieldBody(field, field.section_id)
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.changed.emit(),
        error: e => this.apiError.emit(e),
      });
  }

  onFieldSaved() {
    this.editing.set(null);
    this.changed.emit();
  }

  deleteField(field: CustomWorkflowField) {
    this.service
      .deleteField(this.workflow().id, field.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.changed.emit(),
        error: e => this.apiError.emit(e),
      });
  }

  dropField(
    section: CustomWorkflowSection,
    event: Pick<
      CdkDragDrop<CustomWorkflowField[]>,
      'previousIndex' | 'currentIndex'
    >
  ) {
    if (event.previousIndex === event.currentIndex) return;
    const moved = section.fields[event.previousIndex];
    const ids = section.fields.map(f => f.id);
    moveItemInArray(ids, event.previousIndex, event.currentIndex);

    this.reorderingFieldId.set(moved.id);
    this.service
      .reorderFields(this.workflow().id, section.id, ids)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.reorderingFieldId.set(null))
      )
      .subscribe({
        next: () => {
          this.changed.emit();
        },
        error: e => {
          this.apiError.emit(e);
        },
      });
  }

  addSection() {
    const name = this.transloco.translate('custom_workflow_new_section_name');
    this.service
      .createSection(
        this.workflow().id,
        this.uniqueSectionLabel({ en: name, ar: name })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.changed.emit(),
        error: e => this.apiError.emit(e),
      });
  }

  /** Allowed on locked workflows too; the real form follows section order. */
  moveSection(index: number, delta: -1 | 1) {
    const ids = this.workflow().sections.map(s => s.id);
    moveItemInArray(ids, index, index + delta);
    this.service
      .reorderSections(this.workflow().id, ids)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.changed.emit(),
        error: e => this.apiError.emit(e),
      });
  }

  startRename(section: CustomWorkflowSection) {
    this.renameForm.reset({
      label: {
        en: section.translations.label.en,
        ar: section.translations.label.ar,
      },
    });
    this.renamingId.set(section.id);
  }

  saveRename() {
    const id = this.renamingId();
    if (id === null) return;
    this.formService.syncDualLangControls(this.renameForm);
    if (this.renameForm.invalid) {
      this.renameForm.markAllAsTouched();
      return;
    }
    this.service
      .updateSection(
        this.workflow().id,
        id,
        this.renameForm.getRawValue().label
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.renamingId.set(null);
          this.changed.emit();
        },
        error: e => this.apiError.emit(e),
      });
  }

  onSectionAction(key: string, section: CustomWorkflowSection) {
    if (key === 'duplicate') this.duplicateSection(section);
    else if (key === 'delete') this.deleteSection(section);
  }

  deleteSection(section: CustomWorkflowSection) {
    this.service
      .deleteSection(this.workflow().id, section.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.changed.emit(),
        error: e => this.apiError.emit(e),
      });
  }

  duplicateSection(section: CustomWorkflowSection) {
    const wf = this.workflow();
    this.service
      .createSection(
        wf.id,
        this.duplicateSectionLabel(section.translations.label)
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res =>
          this.duplicateSectionFields(wf.id, res.data.id, section.fields),
        error: e => this.apiError.emit(e),
      });
  }

  /** Strips any existing trailing number so re-duplicating "Main 2" re-numbers from "Main", not "Main 2 2". */
  private duplicateSectionLabel(label: Localized): Localized {
    return this.uniqueSectionLabel({
      en: this.stripTrailingNumber(label.en),
      ar: this.stripTrailingNumber(label.ar),
    });
  }

  /** Titles must be unique within the workflow: the name as-is, else "<name> 2", "<name> 3", and so on. */
  private uniqueSectionLabel(base: Localized): Localized {
    const existing = this.workflow().sections.map(s => s.translations.label);
    const isTaken = (candidate: Localized) =>
      existing.some(
        l =>
          l.en.toLowerCase() === candidate.en.toLowerCase() ||
          l.ar.toLowerCase() === candidate.ar.toLowerCase()
      );
    if (!isTaken(base)) return base;
    let candidate: Localized;
    let i = 2;
    do {
      candidate = { en: `${base.en} ${i}`, ar: `${base.ar} ${i}` };
      i++;
    } while (isTaken(candidate));
    return candidate;
  }

  private stripTrailingNumber(value: string): string {
    return value.replace(/\s+\d+$/, '');
  }

  private duplicateSectionFields(
    workflowId: number,
    sectionId: number,
    fields: CustomWorkflowField[]
  ) {
    if (!fields.length) {
      this.changed.emit();
      return;
    }

    from(fields)
      .pipe(
        concatMap(field =>
          this.service.createField(
            workflowId,
            this.duplicateFieldBody(field, sectionId)
          )
        )
      )
      .subscribe({
        error: e => this.apiError.emit(e),
        complete: () => this.changed.emit(),
      });
  }

  private duplicateFieldBody(
    field: CustomWorkflowField,
    sectionId: number
  ): Record<string, unknown> {
    const type = this.fieldOptions().types.find(t => t.value === field.type);
    const body: Record<string, unknown> = {
      type: field.type,
      key: this.duplicateFieldKey(field.key),
      label: field.translations.label,
      section_id: sectionId,
      mandatory: field.metadata.mandatory,
      hint: localizedOrNull(field.metadata.hint),
    };
    if (type?.has_placeholder)
      body['placeholder'] = localizedOrNull(field.metadata.placeholder);
    if (type?.has_options)
      body['options'] = field.metadata.options.map(o => ({
        value: o.value,
        label: o.label,
      }));
    if (type?.is_media) {
      body['extensions'] = field.metadata.extensions;
      body['max_file_size'] = field.metadata.max_file_size;
    }
    if (type?.is_lookup)
      body['lookup_model_config_id'] = field.metadata.lookup_model_config_id;
    return body;
  }

  private duplicateFieldKey(key: string): string {
    const existing = new Set(
      this.workflow().sections.flatMap(s => s.fields.map(f => f.key))
    );
    let candidate = `${key}_copy`;
    for (let i = 2; existing.has(candidate); i++)
      candidate = `${key}_copy_${i}`;
    return candidate;
  }
}
